import {
  Clock,
  Package,
  ChefHat,
  CheckCircle2,
  User,
  MapPin,
  MoveHorizontal,
  XCircle,
  Loader2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  PackageCheck,
} from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useCloud } from "../hooks/useCloud";
import { usePartnerOrders } from "../hooks/usePartnerOrders";
import PartnerOrderDetailed from "../components/PartnerOrderDetailed";
import PartnerRejectionDialog from "../components/PartnerRejectionDialog";
import CountdownText from "../components/CountdownText";

import store from "../store";
const OrderCard = ({
  order,
  onViewDetails,
  onAccept,
  onMarkReady,
  loading,
}) => {
  const formatTime = (timeString) => {
    return new Date(timeString).toLocaleString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const [isExpanded, setIsExpanded] = useState(false);
  const [prepTime] = useState(order.prepTime.time); // Default 30 minutes

  const toggleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const displayItems = isExpanded ? order.items : order.items.slice(0, 2);
  const hasMoreItems = order.items.length > 2;

  const handleAccept = (e) => {
    e.stopPropagation();
    onAccept(order, prepTime);
  };

  const isZomatoOrder = order.partnerName.toLowerCase() === "zomato";

  return (
    <div className="kanban-card">
      <div className="kanban-card-header">
        <div className="kanban-card-title">
          <span className="text-sm font-bold flex-1">
            Order #{order.orderId}
          </span>
          {order.orderOTP && order.orderOTP !== "NA" && (
            <span className="badge badge--warning ml-2">
              OTP: {order.orderOTP}
            </span>
          )}
        </div>
        <div className="kanban-card-time">{formatTime(order.createdAt)}</div>
      </div>

      <div className="kanban-card-content">
        <div className="kanban-card-info">
          <User className="w-4 h-4" />
          <span>{order.customer.name}</span>
        </div>
        <div className="kanban-card-info">
          <Clock className="w-4 h-4" />
          <span>Prep Time: {order.prepTime.time || 0} min</span>
        </div>

        <div className="kanban-card-items">
          {displayItems.map((item, index) => (
            <div key={index} className="kanban-card-item">
              <div className="flex items-center gap-2">
                {item.isVeg ? (
                  <div class="w-[15px] h-[15px] border border-green-600 rounded-sm flex items-center justify-center">
                    <div class="w-[8px] h-[8px] bg-green-600 rounded-full"></div>
                  </div>
                ) : (
                  <div class="w-[15px] h-[15px] border border-red-600 rounded-sm flex items-center justify-center">
                    <div class="w-[8px] h-[8px] bg-red-600 rounded-full"></div>
                  </div>
                )}
                <span className="flex-1 text-sm">{item.name}</span>
                <span className="text-sm font-medium">×{item.quantity}</span>
              </div>
            </div>
          ))}
          {hasMoreItems && (
            <button
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 w-full justify-center py-1"
              onClick={toggleExpand}
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  <span>Show less</span>
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  <span>+{order.items.length - 2} more items</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="kanban-card-footer">
        <div className="badge badge--neutral">{order.partnerName}</div>
        <div className="kanban-card-actions">
          {order.orderStatus === "NEW" && (
            <>
              <div className="flex items-center gap-2 mr-2">
                <input
                  type="number"
                  min="1"
                  max="120"
                  value={prepTime}
                  // onChange={(e) => setPrepTime(Number(e.target.value))}
                  className="w-16 h-8 px-2 border rounded text-sm"
                  placeholder="Prep time"
                />
                <span className="text-sm">min</span>
              </div>

              <button
                className="kanban-action-button kanban-action-button--primary"
                onClick={handleAccept}
                disabled={(isZomatoOrder && !prepTime) || loading}
              >
                <CheckCircle2 className="w-4 h-4" />
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Accept"
                )}
              </button>
            </>
          )}
          <button
            className="kanban-action-button kanban-action-button--warning"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(order);
            }}
          >
            <MoveHorizontal className="w-4 h-4" />
            Details
          </button>
        </div>
      </div>

      {order.orderStatus === "PREPARING" && (
        <div className="kanban-card-footer">
          <button
            className="kanban-action-button kanban-action-button--success w-full p-2 text-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              onMarkReady(order);
            }}
            disabled={loading}
          >
            <Package className="w-4 h-4" />
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ready"}

            <CountdownText
              startTime={order.updatedAt}
              allowedMinutes={order.prepTime.time}
              onComplete={() => {
                // onMarkReady(order);
              }}
            />
          </button>
        </div>
      )}
    </div>
  );
};

const KanbanColumn = ({
  title,
  orders,
  icon: Icon,
  onViewDetails,
  onAccept,
  onMarkReady,
  loading,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div
      className={`kanban-column ${
        isExpanded ? "kanban-column--expanded" : "kanban-column--collapsed"
      }`}
    >
      <div className="kanban-column-header" onClick={toggleExpand}>
        <h3 className="kanban-column-title">
          <Icon className="w-5 h-5" />
          {title}
        </h3>
        <span className="kanban-column-count">{orders.length}</span>
      </div>
      <div className="kanban-cards-container">
        {orders.length === 0 ? (
          <div className="kanban-empty-state">
            <Package className="w-8 h-8 text-gray-400" />
            <p className="kanban-empty-text">No orders</p>
          </div>
        ) : (
          orders.map((order) => (
            <div key={order.orderId} onClick={() => onViewDetails(order)}>
              <OrderCard
                order={order}
                onViewDetails={onViewDetails}
                onAccept={onAccept}
                onMarkReady={onMarkReady}
                loading={loading}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const PartnerKanbanView = ({ orders, setOrders }) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [rejectDialog, setRejectDialog] = useState({
    isOpen: false,
    orderToReject: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { quickOrder } = useCloud();
  const { trackOrder } = usePartnerOrders();

  // Group orders by status using useMemo
  const groupedOrders = useMemo(() => {
    return orders.reduce(
      (acc, order) => {
        const status = order.orderStatus.toLowerCase();
        if (["new", "preparing", "ready", "pickedup"].includes(status)) {
          acc[status] = acc[status] || [];
          acc[status].push(order);
        }
        return acc;
      },
      { new: [], preparing: [], ready: [], pickedup: [] }
    );
  }, [orders]);

  useEffect(() => {
    if (orders && orders.length > 0) {
      let cachedOrder = localStorage.getItem(`partnerOrders`);
      if (cachedOrder) {
        cachedOrder = JSON.parse(cachedOrder);
      } else {
        cachedOrder = {};
      }

      for (const order of orders) {
        if (order.orderStatusLevel > 1 && !cachedOrder[order.orderId]) {
          trackOrder(order);
          cachedOrder[order.orderId] = order.orderStatusLevel;
        }
      }

      localStorage.setItem(`partnerOrders`, JSON.stringify(cachedOrder));
    }
  }, [orders]);

  useEffect(() => {
    if (groupedOrders?.new?.length > 0 && !selectedOrder) {
      setSelectedOrder(groupedOrders.new[0]);
    }
  }, [groupedOrders?.new]);

  const handleViewDetails = async (order) => {
    try {
      setSelectedOrder(order);
    } catch (err) {
      console.error("Failed to fetch order details:", err);
      // You might want to show an error toast or message here
    }
  };

  const handleCloseDetails = () => {
    setSelectedOrder(null);
  };

  const updateOrderStatus = async (order, newStatus, prepTime) => {
    try {
      setLoading(true);
      let updatedOrder = order;

      switch (newStatus) {
        case "PREPARING": {
          // Pass prep time to accept method for Zomato orders
          const result = await order.accept(prepTime);
          if (result) {
            updatedOrder.orderStatus = "PREPARING";
          }
          break;
        }
        case "READY": {
          const result = await order.markReady();
          if (result) {
            updatedOrder.orderStatus = "READY";
          }
          break;
        }
        case "REJECTED": {
          const result = await order.reject();
          if (result) {
            updatedOrder.orderStatus = "REJECTED";
          }
          break;
        }
        default:
          throw new Error(`Invalid status: ${newStatus}`);
      }

      // Update the orders list with the new status
      setOrders((prevOrders) =>
        prevOrders.map((o) => (o.orderId === order.orderId ? updatedOrder : o))
      );

      return updatedOrder;
    } catch (err) {
      console.error(`Failed to update order status to ${newStatus}:`, err);
      toast.error(
        `Failed to update order status to ${newStatus}: ${err.message}`
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptOrder = async (order, prepTime) => {
    try {
      // For Zomato orders, pass the prep time to the updateOrderStatus function
      await trackOrder({ ...order, orderStatusLevel: 2 });
      await updateOrderStatus(order, "PREPARING", prepTime);

      setSelectedOrder(null);
    } catch (err) {
      // Handle error (show toast/notification)
      console.error("Failed to accept order:", err);
    }
  };

  const handleMarkReady = async (order) => {
    try {
      await trackOrder({ ...order, orderStatusLevel: 3 });
      await updateOrderStatus(order, "READY");
      setSelectedOrder(null);
    } catch (err) {
      // Handle error (show toast/notification)
      console.error("Failed to mark order as ready:", err);
    }
  };

  const handleRejectOrder = (order) => {
    setRejectDialog({
      isOpen: true,
      orderToReject: order,
    });
  };

  const handleConfirmReject = async () => {
    if (rejectDialog.orderToReject) {
      try {
        await updateOrderStatus(rejectDialog.orderToReject, "REJECTED");
        setSelectedOrder(null);
      } catch (err) {
        // Handle error (show toast/notification)
        console.error("Failed to reject order:", err);
      } finally {
        setRejectDialog({
          isOpen: false,
          orderToReject: null,
        });
      }
    }
  };

  const handleCancelReject = () => {
    setRejectDialog({
      isOpen: false,
      orderToReject: null,
    });
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <AlertTriangle className="w-[50px] h-[50px] text-yellow-300" />
        <div className="text-lg font-medium text-gray-700">{error}</div>
      </div>
    );
  }

  return (
    <>
      {store.partnersConnectionError.length > 0 && (
        <div className="flex flex-col items-center justify-center gap-4 m-4 bg-white rounded-md p-4 border border-solid border-yellow-300">
          <AlertTriangle className="w-[50px] h-[50px] text-yellow-300" />
          <div className="text-lg font-medium text-gray-700">
            {store.partnersConnectionError.map((error) => error).join(", ")}{" "}
            partner{store.partnersConnectionError.length > 1 ? "s" : ""} not
            connected
          </div>
        </div>
      )}

      <div className="kanban-container">
        <KanbanColumn
          title="New Orders"
          orders={groupedOrders.new}
          icon={Package}
          onViewDetails={handleViewDetails}
          onAccept={handleAcceptOrder}
          onMarkReady={handleMarkReady}
          loading={loading}
        />
        <KanbanColumn
          title="Preparing"
          orders={groupedOrders.preparing}
          icon={ChefHat}
          onViewDetails={handleViewDetails}
          onAccept={handleAcceptOrder}
          onMarkReady={handleMarkReady}
          loading={loading}
        />
        <KanbanColumn
          title="Ready"
          orders={groupedOrders.ready}
          icon={CheckCircle2}
          onViewDetails={handleViewDetails}
          onAccept={handleAcceptOrder}
          onMarkReady={handleMarkReady}
          loading={loading}
        />
        <KanbanColumn
          title="Picked Up"
          orders={groupedOrders.pickedup || []}
          icon={PackageCheck}
          onViewDetails={handleViewDetails}
          onAccept={handleAcceptOrder}
          onMarkReady={handleMarkReady}
          loading={loading}
        />
      </div>

      {selectedOrder && (
        <PartnerOrderDetailed
          isOpen={true}
          onClose={handleCloseDetails}
          orderData={selectedOrder}
          onAcceptOrder={handleAcceptOrder}
          onMarkReady={() => handleMarkReady(selectedOrder)}
          onRejectOrder={() => handleRejectOrder(selectedOrder)}
          loading={loading}
        />
      )}

      <PartnerRejectionDialog
        isOpen={rejectDialog.isOpen}
        onClose={handleCancelReject}
        order={rejectDialog.orderToReject}
        onGoToPartnerApp={() => {
          const partnerUrl =
            rejectDialog.orderToReject?.partnerName?.toLowerCase() === "swiggy"
              ? "https://partner.swiggy.com"
              : "https://www.zomato.com/partners";
          window.open(partnerUrl, "_blank");
          handleCancelReject();
        }}
      />
    </>
  );
};

export default PartnerKanbanView;
